import { Tag } from '@carbon/react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { StatusTag } from '../components/StatusTag';
import { statusTagType } from '../components/statusTagType';

/** The order's own status (ADR 0088): open, completed, voided, cancelled or merged. */
export function OrderStatusTag({ status }: { status: string }) {
  const { t } = useTranslation();
  return <StatusTag status={status}>{t(`orders.status.${status}` as I18nKey, { defaultValue: status })}</StatusTag>;
}

export interface OrderStatuses {
  orderStatus: string;
  paymentStatus: string;
  kitchenStatus: string;
  fulfilmentStatus?: string | null;
}

/**
 * An order's statuses side by side (ADR 0088): the order itself; while it is open, its payment and the kitchen; its fulfilment
 * whenever getting it to the customer is tracked. Each tag says which status it is to screen readers and on hover.
 */
export function OrderStatusTags({ order }: { order: OrderStatuses }) {
  const { t } = useTranslation();
  const open = order.orderStatus === 'open';
  const tags: { kind: 'payment' | 'kitchen' | 'fulfilment'; status: string }[] = [
    ...(open ? [{ kind: 'payment' as const, status: order.paymentStatus }, { kind: 'kitchen' as const, status: order.kitchenStatus }] : []),
    ...(order.fulfilmentStatus ? [{ kind: 'fulfilment' as const, status: order.fulfilmentStatus }] : []),
  ];
  return (
    <span className="pos-order-statuses">
      <OrderStatusTag status={order.orderStatus} />
      {tags.map(({ kind, status }) => {
        const label = t(`orders.${kind}Status.${status}` as I18nKey, { defaultValue: status });
        const named = `${t(`orders.statusKinds.${kind}` as I18nKey)}: ${label}`;
        return (
          <Tag key={kind} type={statusTagType(status)} title={named} aria-label={named}>
            {label}
          </Tag>
        );
      })}
    </span>
  );
}
